import i18n from "../i18n";

/**
 * obligation-display.ts — PPP Stream A, DLD §A3
 *
 * Maps DB obligation statuses to 3 user-facing display states.
 * This is the single source of truth for obligation rendering — no status
 * badges, no "Estimated" / "Pending entry" pills in JSX.
 *
 * DB statuses:
 *   confirmed          → date (operator-verified date)
 *   needs_confirmation → blank (anchor data missing)
 *   not_required       → not_required
 *   indefinite         → not_required (with i18n label)
 */

/**
 * Structural subset of Obligation consumed by toDisplay().
 * Decoupled from the full Obligation type so matrix and editor code can
 * construct synthetic inputs without `as any` casts.
 */
export interface ObligationDisplayInput {
  status: string;
  nextDueDate?: string | null;
  explanation?: string | null;
  referenceNote?: string | null;
  schedule?: { referenceNote?: string | null } | null;
}

export type ObligationDisplay =
  | { kind: "date"; date: string; referenceNote: string | null }
  | { kind: "blank"; hint: string; referenceNote: string | null }
  | { kind: "not_required"; label?: string };

/** Convert one DB obligation row to a render-ready display descriptor. */
export function toDisplay(obligation: ObligationDisplayInput): ObligationDisplay {
  const status = obligation.status;
  const nextDueDate = obligation.nextDueDate ?? null;
  // referenceNote is either on the obligation directly or on its schedule
  const ref: string | null =
    obligation.referenceNote ?? obligation.schedule?.referenceNote ?? null;
  // explanation is on the obligation directly (top-level field, not on schedule)
  const explanation: string | null = obligation.explanation ?? null;

  if (status === "not_required") {
    return { kind: "not_required" };
  }

  if (status === "indefinite") {
    return { kind: "not_required", label: i18n.t("obligations:status_indefinite_label") };
  }

  if (status === "confirmed") {
    if (nextDueDate) {
      return { kind: "date", date: nextDueDate, referenceNote: ref };
    }
    const hint = explanation ?? i18n.t("obligations:hint_confirmed_no_date");
    return { kind: "blank", hint, referenceNote: ref };
  }

  // needs_confirmation
  const hint =
    explanation ?? i18n.t("obligations:hint_needs_confirmation");
  return { kind: "blank", hint, referenceNote: ref };
}
